// Default cut types — ported from the demo's CUT_TYPES + CUT_ADDONS catalog.
// Phase categories ('cat') drive the per-phase tip rotation. See phaseTips.ts.
//
// In v1 these are seed data: the user starts with the four built-in cut types and
// can later customize them in Settings → Cut Timer (deferred to Week 2). The cut
// timer always deep-clones the chosen cut type before mutating it, so the catalog
// here is treated as immutable at runtime.

export type PhaseCategory =
  | 'setup'
  | 'bulk'
  | 'fade'
  | 'lineup'
  | 'finish'
  | 'beard'
  | 'hottowel'
  | 'shave'
  // 'single' is the synthetic category used when a barber turns "Use phases"
  // OFF in the editor — the cut collapses to one big "Cut" phase carrying
  // the full duration. Detected by addAddon() to fold addon time into the
  // single phase's target instead of appending phases (which would defeat
  // the single-phase choice).
  | 'single';

export type CutPhase = {
  // Canonical English label — also stored in `visits.cut_type` when a phase
  // ends up composed into the visit row. Display layers translate via `nameKey`.
  name: string;
  // i18n dictionary key (e.g. 'cut.phase.setupCape') resolved at render time.
  // Optional so user-customized phases (later in v1.1) can fall back to `name`.
  nameKey?: string;
  sec: number; // target duration in seconds
  cat: PhaseCategory;
};

export type CutType = {
  id: string;
  name: string;
  // Display key — same fallback semantics as CutPhase.nameKey.
  nameKey?: string;
  icon: 'scissors' | 'kit' | 'razor' | 'baby' | 'bolt';
  color: 'gold' | 'red' | 'navy';
  priceCents: number;
  phases: CutPhase[];
};

export type CutAddon = {
  id: string;
  name: string;
  nameKey?: string;
  icon: 'razor' | 'kit' | 'bolt' | 'scissors';
  priceCents: number;
  phases: CutPhase[];
};

export const CUT_TYPES: readonly CutType[] = [
  {
    id: 'quick-fade',
    name: 'Quick fade',
    nameKey: 'cut.type.quickFade',
    icon: 'scissors',
    color: 'gold',
    priceCents: 3500,
    phases: [
      { name: 'Setup & cape', nameKey: 'cut.phase.setupCape', sec: 90, cat: 'setup' },
      { name: 'Bulk removal', nameKey: 'cut.phase.bulkRemoval', sec: 360, cat: 'bulk' },
      { name: 'Fade & blend', nameKey: 'cut.phase.fadeBlend', sec: 360, cat: 'fade' },
      { name: 'Line-up & finish', nameKey: 'cut.phase.lineupFinish', sec: 240, cat: 'finish' },
    ],
  },
  {
    id: 'full-service',
    name: 'Full service',
    nameKey: 'cut.type.fullService',
    icon: 'kit',
    color: 'red',
    priceCents: 6500,
    phases: [
      { name: 'Setup & consult', nameKey: 'cut.phase.setupConsult', sec: 120, cat: 'setup' },
      { name: 'Cut & shape', nameKey: 'cut.phase.cutShape', sec: 600, cat: 'bulk' },
      { name: 'Beard work', nameKey: 'cut.phase.beardWork', sec: 360, cat: 'beard' },
      { name: 'Line-up', nameKey: 'cut.phase.lineup', sec: 300, cat: 'lineup' },
      { name: 'Hot towel & finish', nameKey: 'cut.phase.hotTowelFinish', sec: 420, cat: 'finish' },
    ],
  },
  {
    id: 'beard-cut',
    name: 'Beard + line-up',
    nameKey: 'cut.type.beardLineup',
    icon: 'razor',
    color: 'navy',
    priceCents: 2500,
    phases: [
      { name: 'Setup', nameKey: 'cut.phase.setup', sec: 60, cat: 'setup' },
      { name: 'Beard shape', nameKey: 'cut.phase.beardShape', sec: 360, cat: 'beard' },
      { name: 'Line-up', nameKey: 'cut.phase.lineup', sec: 240, cat: 'lineup' },
      { name: 'Hot towel', nameKey: 'cut.phase.hotTowel', sec: 180, cat: 'hottowel' },
    ],
  },
  {
    id: 'kids-cut',
    name: "Kid's cut",
    nameKey: 'cut.type.kidsCut',
    icon: 'baby',
    color: 'gold',
    priceCents: 2500,
    phases: [
      { name: 'Settle in', nameKey: 'cut.phase.settleIn', sec: 60, cat: 'setup' },
      { name: 'Quick cut', nameKey: 'cut.phase.quickCut', sec: 480, cat: 'bulk' },
      { name: 'Tidy & finish', nameKey: 'cut.phase.tidyFinish', sec: 180, cat: 'finish' },
    ],
  },
] as const;

// Add-ons NEVER include a setup phase — setup happens once at the start of the cut.
// Add-on phases are pure work-stage phases that flow naturally after the main cut.
export const CUT_ADDONS: readonly CutAddon[] = [
  {
    id: 'addon-beard',
    name: 'Beard trim & line-up',
    nameKey: 'cut.addon.beard',
    icon: 'razor',
    priceCents: 1500,
    phases: [
      { name: 'Beard shape', nameKey: 'cut.phase.beardShape', sec: 360, cat: 'beard' },
      { name: 'Beard line-up', nameKey: 'cut.phase.beardLineup', sec: 240, cat: 'lineup' },
    ],
  },
  {
    id: 'addon-hottowel',
    name: 'Hot towel finish',
    nameKey: 'cut.addon.hotTowel',
    icon: 'kit',
    priceCents: 1000,
    phases: [{ name: 'Hot towel', nameKey: 'cut.phase.hotTowel', sec: 180, cat: 'hottowel' }],
  },
  {
    id: 'addon-shave',
    name: 'Straight razor shave',
    nameKey: 'cut.addon.shave',
    icon: 'razor',
    priceCents: 2500,
    phases: [
      { name: 'Hot towel prep', nameKey: 'cut.phase.hotTowelPrep', sec: 180, cat: 'hottowel' },
      { name: 'Razor shave', nameKey: 'cut.phase.razorShave', sec: 360, cat: 'shave' },
      { name: 'Aftershave', nameKey: 'cut.phase.aftershave', sec: 90, cat: 'finish' },
    ],
  },
  {
    id: 'addon-vip',
    name: 'VIP finish',
    nameKey: 'cut.addon.vip',
    icon: 'bolt',
    priceCents: 2000,
    phases: [
      { name: 'Scalp massage', nameKey: 'cut.phase.scalpMassage', sec: 240, cat: 'hottowel' },
      { name: 'Hot towel', nameKey: 'cut.phase.hotTowel', sec: 180, cat: 'hottowel' },
      { name: 'Aftershave & style', nameKey: 'cut.phase.aftershaveStyle', sec: 120, cat: 'finish' },
    ],
  },
  {
    id: 'addon-eyebrow',
    name: 'Eyebrow cleanup',
    nameKey: 'cut.addon.eyebrow',
    icon: 'scissors',
    priceCents: 800,
    phases: [{ name: 'Brow shape', nameKey: 'cut.phase.browShape', sec: 180, cat: 'lineup' }],
  },
  {
    id: 'addon-detail-lineup',
    name: 'Detailed line-up',
    nameKey: 'cut.addon.detailLineup',
    icon: 'scissors',
    priceCents: 1000,
    phases: [{ name: 'Precision line-up', nameKey: 'cut.phase.precisionLineup', sec: 240, cat: 'lineup' }],
  },
] as const;

export function findCutType(id: string): CutType | undefined {
  return CUT_TYPES.find((c) => c.id === id);
}

export function findAddon(id: string): CutAddon | undefined {
  return CUT_ADDONS.find((a) => a.id === id);
}

/**
 * Convert a server-side ServiceRow shape into a CutType / CutAddon usable by
 * the live Cut Timer. Settings → Cut Timer manages a `services` table whose
 * rows store user-customized cut types + add-ons; this is the bridge so the
 * picker can union them with the built-in catalog.
 *
 * The shape we accept is structural so this fn doesn't need to import from
 * @busy/api (which would create a circular dep).
 */
type ServiceRowLike = {
  id: string;
  name: string;
  price_cents: number;
  phases: Array<{ name: string; sec: number; cat: string }>;
  is_addon: boolean;
  icon: 'scissors' | 'kit' | 'razor' | 'baby' | 'bolt';
};

export function serviceRowToCutType(row: ServiceRowLike): CutType {
  return {
    id: row.id,
    name: row.name,
    icon: row.icon,
    color: 'gold',
    priceCents: row.price_cents,
    phases: row.phases.map((p) => ({
      name: p.name,
      sec: p.sec,
      cat: p.cat as CutPhase['cat'],
    })),
  };
}

export function serviceRowToCutAddon(row: ServiceRowLike): CutAddon {
  return {
    id: row.id,
    name: row.name,
    icon: row.icon === 'baby' ? 'kit' : row.icon, // CutAddon icon enum is narrower
    priceCents: row.price_cents,
    phases: row.phases.map((p) => ({
      name: p.name,
      sec: p.sec,
      cat: p.cat as CutPhase['cat'],
    })),
  };
}

export function totalTargetSec(phases: CutPhase[]): number {
  return phases.reduce((sum, p) => sum + p.sec, 0);
}

export function fmtMMSS(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}
